<script lang="ts">
    import { Button, DatePicker, DatePickerInput, Modal, Select, SelectItem, StructuredList, StructuredListBody, StructuredListCell, StructuredListHead, StructuredListRow, StructuredListSkeleton } from "carbon-components-svelte";
    import type { Repository } from "../libraries/repository";
    import type { RepositoryResponse } from "../model/repositoryResponse";
    import { Constants } from "../utils/constants";
    import NotificationToast from "./NotificationToast.svelte";
    import { Utils } from "../utils/utils";
    import { onMount } from "svelte";
    import Edit from "carbon-icons-svelte/lib/Edit.svelte";
    import Download from "carbon-icons-svelte/lib/Download.svelte";
    import TrashCan from "carbon-icons-svelte/lib/TrashCan.svelte";
    import { DocumentType } from "../model/type";
    import { config } from "../config";
    import { navigate } from "svelte-navigator";
    import { LocalStorage } from "../libraries/localStorage";
    import dayjs from "dayjs";
    import type { User } from "../model/user";

    export let repository:Repository;
    export let user: User;
    let getTemplatesResponse:RepositoryResponse;
    let deleteTemplateResponse:RepositoryResponse;
    let promise: Promise<any>;
    let open: boolean = false;
    let templateUuidToDelete: string;
    let dateFilter =  null;
    let typeFilter = null;
    $: showDownloadError = false;
    $: showDeleteError = false;
    $: showDeleteSuccess = false;
    $: filteredTemplates = [];
    $: if(dateFilter != null && dateFilter != ""){ 
        if(getTemplatesResponse){
            filteredTemplates = getTemplatesResponse.data.filter(t => Utils.dateFromTimestamp(t.lastUpdate) == dateFilter);
        }       
    }
    $: if(typeFilter != null && typeFilter != ""){ 
        if(getTemplatesResponse){
            filteredTemplates = getTemplatesResponse.data.filter(t => t.type == typeFilter);
        }
    }

    $: if(typeFilter != null && typeFilter != "" && dateFilter != null && dateFilter != ""){ 
        if(getTemplatesResponse){
            filteredTemplates = getTemplatesResponse.data.filter(t => t.type == typeFilter && Utils.dateFromTimestamp(t.lastUpdate) == dateFilter);
        }
    }

    onMount(() => {
        promise = getTemplates();
    })

    async function getTemplates(){
        getTemplatesResponse = await repository.getTemplates();
        filteredTemplates = getTemplatesResponse.data;
    }

    async function downloadTemplate(templateUuid: string){
        let url = config.baseUrl + "templates/file/" + templateUuid;
        let expire = LocalStorage.getSessionExpire();
        if(dayjs().isAfter(dayjs(expire))){
            repository.logOut();
        }else{
            let token: string = LocalStorage.getUserStorage().token;
            try{
            let response = await fetch(url, {
                    method: "GET",
                    headers: {
                        "Content-type" : "application/json",
                        "X-Api-Key" : token
                    }
                });
                if(response.ok){
                    let blob = await response.blob();
                    let blobUrl = window.URL.createObjectURL(blob);
                    let contentDisposition = response.headers.get("content-disposition");
                    let fileName = contentDisposition.match(/filename="(.+)"/)[1];
                    const a = document.createElement("a")
                    document.body.appendChild(a)
                    a.setAttribute("style", "display: none");
                    a.href = blobUrl;
                    a.download = fileName;
                    a.click()
                    window.URL.revokeObjectURL(blobUrl);
                }
            }catch(error){
                console.error(error);
                showDownloadError = true;
            }
        } 
    }

    async function deleteTemplate(templateUuid: string){
        deleteTemplateResponse = await repository.deleteTemplate(templateUuid);
        if(deleteTemplateResponse.success){
            showDeleteSuccess = true;
            promise = null;
            getTemplatesResponse = null;
            promise = getTemplates();
        }else{
            showDeleteError = true;
        }
    }

</script>

<!-- Modal to delete template -->
<Modal 
  danger
  bind:open modalHeading="Eliminazione template"
  primaryButtonText="Conferma" 
  secondaryButtonText="Annulla"
  on:click:button--primary={() => { deleteTemplate(templateUuidToDelete); open = false; }}
  on:click:button--secondary={() => open = false}
  on:close={() => open = false}>
  Confermi di voler eliminare questo template?
</Modal>
<!-- end -->

<h2 class="mb-1">Template manleve</h2>
<div class="base-flex">
    <DatePicker bind:value={dateFilter} dateFormat="d/m/Y" datePickerType="single" >
        <DatePickerInput size="sm" labelText="Filtra in base all'ultimo aggiornamento" />
    </DatePicker>
    <Select size="sm" labelText="Filtra in base alla tipologia" on:change={(e) => { typeFilter = e.detail; }}>
        <SelectItem value="" />
        {#each Object.keys(DocumentType) as key}
            <SelectItem value={DocumentType[key]}></SelectItem>
        {/each}
    </Select>
    <Button kind="tertiary" on:click={() => {
        filteredTemplates = getTemplatesResponse.data; 
        dateFilter = null; 
        typeFilter = null; 
        }}>Reset</Button>
</div>
{#await promise}
    <StructuredListSkeleton rows={3}/>
{:then}
    {#if getTemplatesResponse != null}
        {#if getTemplatesResponse.success}
        {#if showDownloadError}
        <div class="toast-notification">
            <NotificationToast title="Errore" subtitle="Si è verificato un errore durante il download del file." kind="error" onClose={() => showDownloadError = false} />
        </div>
        {/if}  
        {#if showDeleteError}
        <div class="toast-notification">
            <NotificationToast title="Errore" subtitle={Utils.errorMessage(deleteTemplateResponse)} kind="error" onClose={() => showDeleteError = false} />
        </div>
        {/if}    
        {#if showDeleteSuccess}
        <div class="toast-notification">
            <NotificationToast title="" subtitle="Il template manleva è stato eliminato con successo." kind="success" onClose={() => showDeleteSuccess = false} />
        </div>
        {/if}    
        <StructuredList class="mt-3" condensed>
            <StructuredListHead>
                <StructuredListRow head>
                    <StructuredListCell head>#</StructuredListCell> 
                    {#each Constants.templateHeaders as headerName, index}
                        <StructuredListCell head>{headerName}</StructuredListCell> 
                    {/each}
                </StructuredListRow>
            </StructuredListHead>
            <StructuredListBody>
                {#if filteredTemplates.length === 0}
                <p class="mt-2 mb-2">Non sono presenti template manleve.</p>
                {:else}
                {#each filteredTemplates as template, index}
                    <StructuredListRow class={index%2 === 0 ? "even-row" : ""}>
                        <StructuredListCell>{index + 1}</StructuredListCell>
                        <StructuredListCell>{Utils.returnValueOrAlternativeValue(template.description, "-")}</StructuredListCell>
                        <StructuredListCell>{Utils.returnValueOrAlternativeValue(template.notes, "-")}</StructuredListCell>
                        <StructuredListCell>{template.type}</StructuredListCell>                  
                        <StructuredListCell>{Utils.returnValueOrAlternativeValue(template.operator, "-")}</StructuredListCell>
                        <StructuredListCell>{Utils.returnValueOrAlternativeValue(Utils.dateFromTimestamp(template.lastUpdate), "-")}</StructuredListCell>
                        <StructuredListCell>
                            <Button size="small" kind="ghost" iconDescription="Scarica" icon={Download} on:click={() => { downloadTemplate(template.uuid); }}/>
                            {#if user.role === "SPECIALADMIN"}
                            <Button size="small" kind="ghost" iconDescription="Modifica" icon={Edit} on:click={() => { navigate("/templates/update/" + template.uuid)}}/>
                            <Button size="small" kind="ghost" iconDescription="Elimina" icon={TrashCan} on:click={() => { open = true; templateUuidToDelete = template.uuid }}/>
                            {/if}
                        </StructuredListCell>
                    </StructuredListRow>
                {/each}               
                {/if}
            </StructuredListBody>
        </StructuredList>
        {:else}
        <NotificationToast title="Errore" subtitle={Utils.errorMessage(getTemplatesResponse)} kind="error" onClose={null} />
        {/if}
    {/if}
{/await}
